import { Dispatch, SetStateAction, useMemo } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CancelCircleIcon,
  PauseCircleIcon,
  BalanceScaleIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { CursorClick01 } from "untitledui-js-base";
import { ScreeningCriteriaType } from "shared/app/VerificationTemplate/slices/DecisionCriteria/types";
import {
  ApplicantStatus,
  Application,
  ApplicationStatus,
  DecisionAction,
  ExecutionModes,
  RequiresOverridesIdentityVerification
} from "shared/types";
import ProgressInProgressIcon from "shared/icons/progress-in-progress.svg?react";

import { Applicant, CriteriaCheckSettings, Reasons } from "types/application";
import { RecommendationMethod } from "types/landlord/verificationTemplate";
import { ApplicationScoreCard } from "views/applications-id/components/ApplicationScoreCard";
import { StatusCard } from "./components/StatusCard";
import { RuleBasedRecommendation } from "./components/RuleBasedRecommendation";

const InProgressCard = ({ title }: { title: string }) => (
  <StatusCard
    variant="banner"
    icon={<ProgressInProgressIcon width={56} height={56} />}
    title={title}
    subtitle="Recommendation will be ready once the application and all reports are complete"
  />
);

const WithWrapper = ({
  children,
  wrapperClassName
}: {
  children: React.ReactNode;
  wrapperClassName?: string;
}) => {
  if (wrapperClassName)
    return <div className={wrapperClassName}>{children}</div>;

  return children;
};

// Terminal application statuses that pre-empt any recommendation card,
// regardless of decision/verification data.
const TERMINAL_STATUS_CARDS: Partial<Record<ApplicationStatus, JSX.Element>> = {
  [ApplicationStatus.Canceled]: (
    <StatusCard
      variant="badge"
      icon={
        <HugeiconsIcon
          icon={CancelCircleIcon}
          className="text-content-300"
          size={40}
          strokeWidth={2}
        />
      }
      title="Application canceled"
      subtitle="No new data will be added and no reports will be run"
    />
  ),
  [ApplicationStatus.OnHold]: (
    <StatusCard
      variant="badge"
      icon={
        <HugeiconsIcon
          icon={PauseCircleIcon}
          className="text-content-300"
          size={40}
          strokeWidth={2}
        />
      }
      title="Application is on hold"
      subtitle="Application is on hold for now and can be resumed anytime"
    />
  )
};

export const DecisionCard = ({
  requiresOverridesIdentityVerification,
  applicants,

  ruleReasons,

  screeningCriteria,

  autoApproved,

  criteriaCheckSettings,
  applicationStatus,

  canMakeDecision,

  score,
  recommendation,
  recommendationMethod,

  requiresManualOrConditionalExecution,
  hasPendingVerifications,

  setShowDecisionFlowModal,
  setShowExplainRecommendations,

  wrapperClassName
}: {
  screeningCriteria?: ScreeningCriteriaType;
  criteriaCheckSettings?: CriteriaCheckSettings;
  autoApproved: boolean;
  ruleReasons?: Reasons[];
  applicationStatus: ApplicationStatus;
  applicants: Applicant[];
  requiresOverridesIdentityVerification?: RequiresOverridesIdentityVerification;
  requiresManualOrConditionalExecution?: Application["requires_manual_or_conditional_execution"];
  recommendation?: DecisionAction;
  recommendationMethod?: RecommendationMethod;
  canMakeDecision: boolean;
  score: null | number | undefined;
  hasPendingVerifications: boolean;
  setShowDecisionFlowModal: Dispatch<SetStateAction<boolean>>;
  setShowExplainRecommendations: Dispatch<SetStateAction<boolean>>;

  wrapperClassName?: string;
  // eslint-disable-next-line sonarjs/cognitive-complexity -- branches map 1:1 to distinct card states; already extracted the terminal-status lookup and manual-verification card, further splitting would obscure the state machine
}) => {
  const totalManualVerifications = useMemo(() => {
    return (
      requiresManualOrConditionalExecution?.applicants?.reduce(
        (count, applicant) => {
          const manualVerificationsCount = applicant.verifications.filter(
            (verification) =>
              verification.execution_mode === ExecutionModes.Manual
          ).length;

          return count + manualVerificationsCount;
        },
        0
      ) || 0
    );
  }, [requiresManualOrConditionalExecution]);

  const scoreBased = recommendationMethod === "score_based",
    ruleBased = recommendationMethod === "rule_based",
    noRecommendationEnabled = !scoreBased && !ruleBased;

  const terminalStatusCard = TERMINAL_STATUS_CARDS[applicationStatus];
  if (terminalStatusCard)
    return (
      <WithWrapper wrapperClassName={wrapperClassName}>
        {terminalStatusCard}
      </WithWrapper>
    );

  // show the in-progress card if application status is not submitted or decision
  // or if magic links are present
  // or if any applicant is not submitted
  if (
    [
      ApplicationStatus.PartiallySubmitted,
      ApplicationStatus.Started,
      ApplicationStatus.Pending
    ].includes(applicationStatus) ||
    applicants.some(
      (applicant) =>
        ![ApplicantStatus.Submitted, ApplicantStatus.Canceled].includes(
          applicant.application_status
        )
    )
  )
    return (
      <WithWrapper wrapperClassName={wrapperClassName}>
        <InProgressCard title="Application in progress" />
      </WithWrapper>
    );

  const hasRuleDecisionData =
    ruleBased && (Boolean(recommendation) || (ruleReasons?.length || 0) > 0);
  const hasScoreDecisionData = scoreBased && score != null;

  if (
    totalManualVerifications > 0 &&
    !hasRuleDecisionData &&
    !hasScoreDecisionData
  )
    return (
      <WithWrapper wrapperClassName={wrapperClassName}>
        <StatusCard
          variant="banner"
          icon={<CursorClick01 className="stroke-primary-500" size="56" />}
          title={`${totalManualVerifications} reports require manual run`}
          subtitle={
            scoreBased || ruleBased
              ? "Decision will be recommended after all reports have been run"
              : undefined
          }
        />
      </WithWrapper>
    );

  if (hasPendingVerifications && !hasRuleDecisionData && !hasScoreDecisionData)
    return (
      <WithWrapper wrapperClassName={wrapperClassName}>
        <InProgressCard title="Screening in progress" />
      </WithWrapper>
    );

  if (noRecommendationEnabled || (hasPendingVerifications && !recommendation))
    return (
      <WithWrapper wrapperClassName={wrapperClassName}>
        <StatusCard
          variant="badge"
          icon={
            <HugeiconsIcon
              icon={BalanceScaleIcon}
              className="text-content-300"
              size={40}
            />
          }
          title="No recommendation"
          subtitle={
            hasPendingVerifications && !recommendation
              ? "Some verifications are still pending"
              : "Decision recommendations have been disabled in the screening settings for this application"
          }
        />
      </WithWrapper>
    );

  if (scoreBased && hasScoreDecisionData)
    return (
      <WithWrapper wrapperClassName={wrapperClassName}>
        <ApplicationScoreCard
          applicationScore={score}
          recommendation={recommendation}
          onButtonClick={() => setShowDecisionFlowModal(true)}
          criteriaCheckSettings={criteriaCheckSettings}
          applicationSubmitted={
            applicationStatus === ApplicationStatus.Submitted
          }
          templateCriteriaSettings={screeningCriteria}
          applicants={applicants}
          requires_overrides_identity_verification={
            requiresOverridesIdentityVerification
          }
          status={applicationStatus}
        />
      </WithWrapper>
    );

  if (hasRuleDecisionData)
    return (
      <WithWrapper wrapperClassName={wrapperClassName}>
        <RuleBasedRecommendation
          recommendation={recommendation}
          autoApproved={autoApproved}
          setShowExplainRecommendations={setShowExplainRecommendations}
          setShowDecisionFlowModal={setShowDecisionFlowModal}
          canMakeDecision={canMakeDecision}
          ruleReasons={ruleReasons}
          applicationStatus={applicationStatus}
        />
      </WithWrapper>
    );

  return (
    <WithWrapper wrapperClassName={wrapperClassName}>
      <InProgressCard title="Screening in progress" />
    </WithWrapper>
  );
};
